import React, { useState } from 'react';
import { MessageSquare, Bug, Lightbulb, MessageCircle } from 'lucide-react';
import axios from 'axios';
import SEO from './SEO';

interface FeedbackFormData {
  name?: string;
  email: string;
  category: 'bug' | 'feature_request' | 'general';
  subject: string;
  message: string;
}

export default function Feedback() {
  const [formData, setFormData] = useState<FeedbackFormData>({
    name: '',
    email: '',
    category: 'general',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState<{ type: 'success' | 'error' | null; message: string }>({
    type: null,
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Build-time injected via Vite (Docker ARG -> ENV) defaults to localhost:8000; direct backend access
  const apiBaseRaw = (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL || 'http://localhost:8000';
  const apiBase = apiBaseRaw.replace(/\/$/, '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus({ type: null, message: '' });
    setIsSubmitting(true);

    try {
      await axios.post(`${apiBase}/feedback`, {
        ...formData,
        name: formData.name || undefined  // Don't send empty string
      });

      setStatus({
        type: 'success',
        message: 'Thank you for your feedback! We appreciate your input and will review it shortly.'
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        category: 'general',
        subject: '',
        message: ''
      });
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.data?.detail) {
        setStatus({
          type: 'error',
          message: `Error: ${error.response.data.detail}`
        });
      } else {
        setStatus({
          type: 'error',
          message: 'There was an error submitting your feedback. Please try again.'
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  // SEO structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Feedback - Local Artist Fairs",
    "description": "Submit feedback, bug reports, or feature requests to help improve Local Artist Fairs"
  };

  return (
    <>
      <SEO
        title="Feedback - Local Artist Fairs"
        description="Submit feedback, bug reports, or feature requests to help improve Local Artist Fairs. We value your input!"
        keywords="feedback, bug report, feature request, suggestions, local artist fairs feedback"
        url="https://localartistfairs.com/feedback"
        structuredData={structuredData}
      />

      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary-dark mb-2">We Value Your Feedback</h1>
          <p className="text-gray-600">
            Help us improve Local Artist Fairs by sharing your thoughts, reporting bugs, or suggesting new features
          </p>
        </div>

        {/* Feedback Categories Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow-md p-4 text-center">
            <Bug className="h-8 w-8 text-red-600 mx-auto mb-2" />
            <h3 className="font-semibold text-gray-800 mb-1">Report a Bug</h3>
            <p className="text-sm text-gray-600">
              Found something not working? Let us know!
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 text-center">
            <Lightbulb className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
            <h3 className="font-semibold text-gray-800 mb-1">Feature Request</h3>
            <p className="text-sm text-gray-600">
              Have an idea for improvement? Share it!
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 text-center">
            <MessageCircle className="h-8 w-8 text-secondary mx-auto mb-2" />
            <h3 className="font-semibold text-gray-800 mb-1">General Feedback</h3>
            <p className="text-sm text-gray-600">
              Any other comments or suggestions?
            </p>
          </div>
        </div>

        {/* Feedback Form */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-4">
            <MessageSquare className="h-6 w-6 text-secondary mr-2" />
            <h2 className="text-xl font-semibold text-primary-dark">Submit Feedback</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {status.type && (
              <div
                className={`p-3 rounded-md ${
                  status.type === 'success'
                    ? 'bg-teal-100 text-primary-dark'
                    : 'bg-red-100 text-red-700'
                }`}
              >
                {status.message}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                placeholder="your.email@example.com"
              />
              <p className="text-xs text-gray-500 mt-1">
                We'll use this to follow up on your feedback
              </p>
            </div>

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Name (optional)
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                placeholder="Your name"
              />
            </div>

            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                id="category"
                value={formData.category}
                onChange={handleChange}
                required
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              >
                <option value="general">General Feedback</option>
                <option value="bug">Bug Report</option>
                <option value="feature_request">Feature Request</option>
              </select>
            </div>

            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                Subject <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                minLength={3}
                maxLength={500}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                placeholder="Brief summary of your feedback"
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.subject.length}/500 characters
              </p>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                id="message"
                rows={6}
                value={formData.message}
                onChange={handleChange}
                required
                minLength={10}
                maxLength={5000}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                placeholder="Please provide details about your feedback..."
              ></textarea>
              <p className="text-xs text-gray-500 mt-1">
                {formData.message.length}/5000 characters
              </p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-2 px-4 rounded-md transition-colors ${
                isSubmitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-primary hover:bg-primary-dark'
              } text-white`}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </form>

          <div className="mt-6 p-4 bg-moonbone rounded-md">
            <p className="text-sm text-ink">
              <strong>Note:</strong> Your feedback will be reviewed by our team. For urgent issues or
              direct support, please use our {' '}
              <a href="/contact" className="text-secondary hover:text-primary-dark underline">
                Contact form
              </a>.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
